import {
  EMPTY_DATA_VIEW_STATE,
  type DataViewGroupBy,
  type DataViewLayout,
  type DataViewState,
  type ProjectTaskGroupBy,
  type ProjectTaskSort,
  type ProjectTaskSortColumn,
  type ProjectTaskSortDirection,
} from "./viewDefinition";

const VIEW_LAYOUTS: ReadonlySet<DataViewLayout> = new Set(["list", "board", "timeline"]);
const VIEW_GROUP_BY: ReadonlySet<DataViewGroupBy> = new Set([
  "none",
  "status",
  "priority",
  "campaign",
  "owner",
]);
const PROJECT_TASK_GROUP_BY: ReadonlySet<ProjectTaskGroupBy> = new Set(["status", "milestone"]);
const PROJECT_TASK_SORT_COLUMNS: ReadonlySet<ProjectTaskSortColumn> = new Set([
  "title",
  "assignee",
  "due",
]);
const PROJECT_TASK_SORT_DIRECTIONS: ReadonlySet<ProjectTaskSortDirection> = new Set([
  "asc",
  "desc",
]);

function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

export function normalizeDataViewLayout(value: unknown): DataViewLayout {
  return typeof value === "string" && VIEW_LAYOUTS.has(value as DataViewLayout)
    ? (value as DataViewLayout)
    : EMPTY_DATA_VIEW_STATE.layout;
}

export function normalizeDataViewGroupBy(value: unknown): DataViewGroupBy {
  return typeof value === "string" && VIEW_GROUP_BY.has(value as DataViewGroupBy)
    ? (value as DataViewGroupBy)
    : EMPTY_DATA_VIEW_STATE.groupBy;
}

function normalizeProjectTaskGroupBy(value: unknown): ProjectTaskGroupBy {
  return typeof value === "string" && PROJECT_TASK_GROUP_BY.has(value as ProjectTaskGroupBy)
    ? (value as ProjectTaskGroupBy)
    : EMPTY_DATA_VIEW_STATE.taskGroupBy;
}

function normalizeProjectTaskSort(value: unknown): ProjectTaskSort | null {
  const [columnId, direction, extra] = readString(value).split(":");
  if (
    extra !== undefined ||
    !PROJECT_TASK_SORT_COLUMNS.has(columnId as ProjectTaskSortColumn) ||
    !PROJECT_TASK_SORT_DIRECTIONS.has(direction as ProjectTaskSortDirection)
  ) {
    return null;
  }
  return {
    columnId: columnId as ProjectTaskSortColumn,
    direction: direction as ProjectTaskSortDirection,
  };
}

export function parseDataViewSearch(search: Record<string, unknown>): {
  readonly savedViewId: string;
  readonly state: DataViewState;
} {
  const legacyView = search["view"];
  return {
    savedViewId: readString(search["savedView"]),
    state: {
      ...EMPTY_DATA_VIEW_STATE,
      layout: normalizeDataViewLayout(search["layout"] ?? legacyView),
      search: readString(search["q"]),
      statusFilter: readString(search["status"]),
      workspaceFilter: readString(search["workspace"]),
      campaignFilter: readString(search["campaign"]),
      ownerFilter: readString(search["owner"]),
      groupBy: normalizeDataViewGroupBy(search["group"]),
    },
  };
}

export function serializeDataViewSearch(
  state: DataViewState,
  savedViewId: string | null,
): Record<string, string | undefined> {
  return {
    view: undefined,
    savedView: savedViewId && savedViewId.length > 0 ? savedViewId : undefined,
    layout: state.layout === EMPTY_DATA_VIEW_STATE.layout ? undefined : state.layout,
    q: state.search.trim().length > 0 ? state.search.trim() : undefined,
    status: state.statusFilter.length > 0 ? state.statusFilter : undefined,
    workspace: state.workspaceFilter.length > 0 ? state.workspaceFilter : undefined,
    campaign: state.campaignFilter.length > 0 ? state.campaignFilter : undefined,
    owner: state.ownerFilter.length > 0 ? state.ownerFilter : undefined,
    group: state.groupBy === EMPTY_DATA_VIEW_STATE.groupBy ? undefined : state.groupBy,
  };
}

export function parseProjectTaskDataViewSearch(search: Record<string, unknown>): {
  readonly savedViewId: string;
  readonly state: DataViewState;
} {
  return {
    savedViewId: readString(search["taskSavedView"]),
    state: {
      ...EMPTY_DATA_VIEW_STATE,
      layout: normalizeDataViewLayout(search["taskLayout"]),
      search: readString(search["taskQ"]),
      statusFilter: readString(search["taskStatus"]),
      ownerFilter: readString(search["taskOwner"]),
      milestoneFilter: readString(search["taskMilestone"]),
      taskGroupBy: normalizeProjectTaskGroupBy(search["taskGroup"]),
      ...(search["taskSort"] !== undefined
        ? { taskSort: normalizeProjectTaskSort(search["taskSort"]) }
        : {}),
    },
  };
}

export function serializeProjectTaskDataViewSearch(
  state: DataViewState,
  savedViewId: string | null,
): Record<string, string | undefined> {
  return {
    taskSavedView: savedViewId && savedViewId.length > 0 ? savedViewId : undefined,
    taskLayout: state.layout === EMPTY_DATA_VIEW_STATE.layout ? undefined : state.layout,
    taskQ: state.search.trim().length > 0 ? state.search.trim() : undefined,
    taskStatus: state.statusFilter.length > 0 ? state.statusFilter : undefined,
    taskOwner: state.ownerFilter.length > 0 ? state.ownerFilter : undefined,
    taskMilestone: state.milestoneFilter.length > 0 ? state.milestoneFilter : undefined,
    taskGroup:
      state.taskGroupBy === EMPTY_DATA_VIEW_STATE.taskGroupBy ? undefined : state.taskGroupBy,
    taskSort:
      state.taskSort != null ? `${state.taskSort.columnId}:${state.taskSort.direction}` : undefined,
  };
}
